<template>
  <div @keydown.esc="clearSelection" tabindex="0">
    <div v-if="isTagSelected || isSelected" class="polygon-controls">
      <div class="polygon-controls__input">
        <button
          v-if="!isCreatingPolygon && polygon.points.length === 0"
          class="polygon-controls__btn"
          @click.stop="startPolygon"
        >Create Polygon</button>
        <button
          v-else
          class="polygon-controls__btn"
          @click.stop="deletePolygon"
        >Delete Polygon</button>
        <button
          v-if="isCreatingPolygon && polygon.points.length > 1"
          class="polygon-controls__btn ml-1"
          @click.stop="endPolygon"
        >Close Polygon</button>
      </div>
    </div>
    <div
      class="polygon-overlay"
      :class="[
        {'polygon-overlay--active': isSelected || isCreatingPolygon},
        {'polygon-overlay--outline': isSelected || isCreatingPolygon},
        {'polygon-overlay--outline-dashed': isHighlighted},
      ]"
      ref="polygonOverlay"
      @mouseup.left.stop="editPointDragEnd($event)"
      @mousemove.left.stop="editPointDrag($event)"
    >
      <svg
        viewBox="0 0 3190 1960"
        height="100%"
        width="100%"
        :fill="editPolygon.fill"
        :stroke="editPolygon.stroke"
        :stroke-width="editPolygon.strokeWidth"
      >
        <polygon
          :class="[
            {'drop-shadow' : editPolygon.dropShadow},
            {'inactive' : isCreatingPolygon},
          ]"
          :points="pointToPolygon"
          :style="`outline-offset: -${editPolygon.strokeWidth}px`"
          @click="selectPolygon"
        />
      </svg> 

      <div v-if="isEditingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'point' + i"
          class="point"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @mousedown.left.stop="editPointDragStart(i)"
          @mouseup.left.stop="editPointDragEnd"
          @dblclick.left.stop="deletePoint(i)"
        ></div>
      </div>

      <div v-if="isAddingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'dot' + i"
          class="dot"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
        ></div>
        <div
          v-for="(point, i) in intermidiatePoints"
          :key="'plus' + i"
          class="plus"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @click.left="addPointToPolygon(point, i)"
        ></div>
      </div>
    </div>

    <draggable-elements-menu v-if="isSelected" ref="draggableElementsMenu">
      <div class="group-inline">
        <label>Fill</label>
        <button
          class="btn-colour"
          :style="`background-color: ${editPolygon.fill}`"
          @click="activePicker === 'fill' ? activePicker = '' : activePicker = 'fill'"
        ></button>
      </div>
      <div class="mb-2">
        <verte v-if="activePicker === 'fill'" display="widget" picker="square" model="rgb" v-model="editPolygon.fill"></verte>
      </div>
      <div class="group-inline">
        <label>Stroke</label>
        <button
          class="btn-colour"
          :style="`background-color: ${editPolygon.stroke}`"
          @click="activePicker === 'stroke' ? activePicker = '' : activePicker = 'stroke'"
        ></button>
      </div>
      <div class="mb-2">
        <verte v-if="activePicker === 'stroke'" display="widget" picker="square" model="rgb" v-model="editPolygon.stroke"></verte>
      </div>
      <div class="group-inline">
        <label>Stroke Width</label>
        <input class="group-inline__input" id="strokeWidth" type="number" max="20" min="0" v-model.number="editPolygon.strokeWidth">
      </div>
      <div class="group-inline">
        <label>Drop Shadow</label>
        <input class="group-inline__input" type="checkbox" :checked="editPolygon.dropShadow" @change="editPolygon.dropShadow = $event.target.checked">
      </div>
      <hr />
      <div class="group-inline mt-auto">
        <button v-if="!isAddingPoints && !isEditingPoints" class="btn btn-xs btn-primary mr-2" @click.stop="isAddingPoints = true">Add Points</button>
        <button v-if="!isEditingPoints && !isAddingPoints" class="btn btn-xs btn-primary mr-2" @click.stop="isEditingPoints = true">Edit Points</button>
        <button v-if="isEditingPoints || isAddingPoints"  class="btn btn-xs btn-warning mr-2" @click.stop="isEditingPoints = false; isAddingPoints = false">Stop Editing</button>
      </div>
      <div class="group-inline ">
        <button class="btn btn-xs btn-danger" @click.stop="deletePolygon">Delete Polygon</button>
      </div>
    </draggable-elements-menu>
  </div>
</template>

<script>
import DraggableElementsMenu from '@/components/DraggableElementsMenu'
import Verte from 'verte'
import 'verte/dist/verte.css'

export default {
  components: {
    DraggableElementsMenu,
    Verte
  },

  props: {
    'polygon': {
      type: Object,
      default: () => ({
        fill: 'rgba(0,0,0,0)',
        stroke: 'rgba(255,255,255,0)',
        strokeWidth: 8,
        points: [],
        dropShadow: false,
      })
    },
    'to': {
      type: String,
      default: ''
    },
    'scale': {
      type: Number,
      default: 1
    },
    'isHighlighted': {
      type: Boolean,
      default: false
    },
    'isSelected': {
      type: Boolean,
      default: false
    },
    'isTagSelected': {
      type: Boolean,
      default: false
    },
  },

  data () {
    return {
      isCreatingPolygon: false,
      isEditingPoints: false,
      isAddingPoints: false,

      editPolygonUpdate: 0,
      editPolygon: {},
      editPolygonPointIndex: null,
      intermidiatePoints: [],

      activePicker: '',
    }
  },

  computed: {
    pointToPolygon () {
      if (this.editPolygon && this.editPolygon.points) {
        return this.editPolygon.points.map(point => `${point[0]},${point[1]}`).join(' ')
      }
    },
  },

  watch: {
    'editPolygon': {
      handler: function (newPolygon) {
        if (newPolygon) {
          this.generateIntermidiatePoints()
          this.$emit('updatePolygon', newPolygon)

        }
      },
      deep: true
    },

    'isTagSelected': {
      handler: function (newIsTagSelected) {
        if (!newIsTagSelected) {
          this.isEditingPoints = false
          this.isAddingPoints = false
        }
      },
      deep: true
    },
  },

  mounted () {
    this.editPolygon = this.polygon
    this.generateIntermidiatePoints()
  },

  methods: {
    generateIntermidiatePoints () {
      this.intermidiatePoints = []
      this.editPolygon.points.forEach((point, i) => {
        if (i !== 0 && i !== this.editPolygon.points.length) {
          let prevPoint = this.editPolygon.points[i - 1]
          let x = (prevPoint[0] + point[0]) / 2
          let y = (prevPoint[1] + point[1]) / 2
          this.intermidiatePoints.push([x, y])
        }

        if (i === this.editPolygon.points.length - 1) {
          let firstPoint = this.editPolygon.points[0]
          let x = (firstPoint[0] + point[0]) / 2
          let y = (firstPoint[1] + point[1]) / 2
          this.intermidiatePoints.push([x, y])
        }
      })
    },

    selectPolygon () {
      if (!this.isCreatingPolygon) {
        this.$emit('selectedPolygon', this.polygon)
      }
    },

    startPolygon () {
      this.isCreatingPolygon = true
    },

    endPolygon () {
      this.isCreatingPolygon = false
    },

    addPointToPolygon (point, i) {
      this.editPolygon.points.splice(i + 1, 0, point)
      this.generateIntermidiatePoints()
    },

    deletePoint (i) {
      this.editPolygon.points.splice(i, 1)
      this.generateIntermidiatePoints()
    },

    editPointDragStart (i) {
      this.editPolygonPointIndex = i
    },

    editPointDragEnd (e) {
      if (this.isCreatingPolygon) {
        const x = e.layerX / this.scale
        const y = e.layerY / this.scale

        this.editPolygon.points.push([x, y])
      } else if (this.isEditingPoints || this.isAddingPoints) {
        this.editPolygonPointIndex = null
      } else {
        this.$emit('selectedPolygon', null)
      }
    },

    editPointDrag (event) {
      if ((this.isEditingPoints || this.isAddingPoints) && this.editPolygonPointIndex !== null) {
        let point = this.editPolygon.points[this.editPolygonPointIndex]

        point[0] = Math.round(point[0] + event.movementX / this.scale)
        point[1] = Math.round(point[1] + event.movementY / this.scale)

        this.$set(this.editPolygon.points, this.editPolygonPointIndex, point)
      }
    },

    deletePolygon () {
      if (window.confirm("Are you sure you want to delete this polygon?")) {
        this.editPolygon.points = []
        this.clearSelection()
      }
    },

    navigationTo () {
      this.$router.push(this.to)
    },

    updateColor (e) {
      this.editPolygon.stroke = e.hex8
    },

    clearSelection () {
      this.isEditingPoints = false
      this.isAddingPoints = false
      this.isCreatingPolygon = false
      this.editPolygonPointIndex = null
    }
  },
}
</script>

<style lang="scss" scoped>
.polygon-overlay {
  pointer-events: none;
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 5;

  &--active {
    cursor: crosshair;
    pointer-events: auto;
    z-index: 10;
  }

  &--outline-dashed {
    svg polygon {
      outline: 5px dashed aqua;
    }
  }

  &--outline {
    svg polygon {
      outline: 5px solid aqua;
    }
  }

  svg polygon {
    pointer-events: auto;
  }

  .drop-shadow {
    filter: drop-shadow(10px 10px 10px black);
  }

  .inactive {
    pointer-events: none;
  }
}

.point {
  position: absolute;
  width: 10px;
  height: 10px;
  background: transparent;
  border: 1px solid #fff;
  cursor: crosshair;
  transform: translate(-5px, -5px);
  z-index: 20;
}

.dot {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #000;
  border: 1px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-3px, -3px);
  z-index: 20;
}

.plus {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 14px;
  position: absolute;
  transform: translate(-7px, -7px);
  width: 14px;
  z-index: 20;
  font-size: 14px;
  color: #000;

  &::after {
    content: '+';
    display: flex;
    line-height: 0;
    padding: 0 0 3px 1px;
  }
}

.polygon-controls {
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  pointer-events: all;
  position: absolute;
  right: 24px;
  top: 4px;
  z-index: 200;

  &__btn {
    appearance: none;
    background-color: #3182ce;
    border: none;
    border-radius: 9999px;
    color: #fff;
    font-size: 12px;
    height: 24px;
    padding: 0 8px;

    &:hover {
      background: #2b6cb0;
    }
  }
}
</style>